import { jwtDecode } from 'jwt-decode';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import type { InvitedUserPayload } from '@weft/types';
import { validateNotNullOrUndefined } from '@weft/utils';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useRegister } from '../../../hooks/requests/use-register';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { AuthBackgroundImage } from '../users-components/AuthBackgroundImage';
import { RegisterPageForm } from './register-page-components/RegisterPageForm';
import type { NewRegisterForm } from './register-page-types/new-register-form-schema';
import { NewRegisterFormSchema } from './register-page-types/new-register-form-schema';
import type { RegisterFormInput } from './register-page-types/register-form-input-schema';

export const RegisterPage: React.FC = () => {
  const navigate = useVyNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  validateNotNullOrUndefined(token, 'token');

  const jwtPayload = jwtDecode<InvitedUserPayload>(token);

  const methods = useForm<RegisterFormInput, unknown, NewRegisterForm>({
    resolver: zodResolver(NewRegisterFormSchema),
    defaultValues: {
      username: jwtPayload.username,
    },
  });

  const registerMutation = useRegister();

  const handleSubmit = methods.handleSubmit(async ({ password }) => {
    await registerMutation.mutateAsync({
      token,
      password,
    });

    return navigate(`/confirm?token=${token}`);
  });

  return (
    <>
      <Helmet>
        <title>Register</title>
      </Helmet>
      <Grid
        container
        style={{ height: '100vh', overflow: 'hidden' }}
      >
        <Grid
          item
          lg={5}
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit}>
              <RegisterPageForm/>
            </form>
          </FormProvider>
        </Grid>
        <AuthBackgroundImage/>
      </Grid>
    </>
  );
};
